import { definedMap } from "@glideapps/ts-necessities";
import flow from "lodash/fp/flow";
import groupBy from "lodash/fp/groupBy";
import sortBy from "lodash/fp/sortBy";
import { makeStartOfDayTimeZoneAgnostic } from "../../date-time/support";
import { ConditionVariable } from "../condition-variable";
import { logInfo } from "../debug-print";
import { assert } from "../Support";
import { CalendarListOrder } from "./calendar-list-order";
import {
    asArrayValue,
    asMaybeBoolean,
    asString,
    AsyncRecomputingValueProducer,
    getValueFromValueProducer,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
    parseValueAsDate,
} from "./component-helpers";
import type {
    Context,
    DeserializableKind,
    DeserializationSpec,
    EnvironmentBoundEvaluable,
    EvalEnvironment,
    Evaluable,
    Loading,
    ObjectInspector,
    ObjectValue,
    PotentialValue,
    ValueProducer,
} from "./computation-types";
import { BindEnvironmentEvaluable, LOADING } from "./computation-types";
import { Constant, Data } from "./data";
import { registerDeserializable } from "./deserialization";
import { formatShortDate } from "./localization";
import { FilterWhereIdenticalValueProducer, TableIndex } from "./table-index";
import type { AppEnvironment, AppOther } from "./types";

// FIXME: This should really be Glide-independent.  We'd need to put the global
// variables into the eval environment for that to work.
class LookupGlobalVariableEvaluable implements Evaluable<AppOther, AppEnvironment> {
    constructor(private readonly _name: string) {}

    public evalInEnvironment(env: EvalEnvironment<AppOther, AppEnvironment>): ValueProducer<AppOther, AppEnvironment> {
        return env.root.appEnvironment.globalVariables.get(this._name);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["LookupGlobalVariable", "evaluable", ["name", "string"]];
    }
}

class GlobalEvaluable implements Evaluable<AppOther, AppEnvironment> {
    constructor(private readonly _name: string, private readonly _body: Evaluable<AppOther, AppEnvironment>) {}

    public evalInEnvironment(
        env: EvalEnvironment<AppOther, AppEnvironment>,
        context: Context<AppOther, AppEnvironment>
    ): ValueProducer<AppOther, AppEnvironment> {
        const existing = env.root.appEnvironment.globalValues.get(this._name);
        if (existing !== undefined) return existing;

        const vp = this._body.evalInEnvironment(env, context);
        env.root.appEnvironment.globalValues.set(this._name, vp);

        return vp;
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Global", "evaluable", ["name", "string"], ["body", "evaluable"]];
    }
}

// FIXME: Implement with a subscription manager, ideally with RecomputingFromListValueProducer
class SortAndGroupCalendarItemsValueProducer<O, A> extends AsyncRecomputingValueProducer<O, A> {
    private _dateTimes: Set<ValueProducer<O, A>> = new Set();

    constructor(
        private readonly _startDateTime: EnvironmentBoundEvaluable<O, A>,
        private readonly _endDateTime: EnvironmentBoundEvaluable<O, A> | undefined,
        private readonly _title: EnvironmentBoundEvaluable<O, A>,
        private readonly _description: EnvironmentBoundEvaluable<O, A> | undefined,
        private readonly _itemID: EnvironmentBoundEvaluable<O, A> | undefined,
        private readonly _list: ValueProducer<O, A>,
        private readonly _order: ValueProducer<O, A> | undefined
    ) {
        super();
    }

    private subscribeDateTimes(): void {
        for (const vp of this._dateTimes) {
            vp.subscribe(this);
        }
    }

    private unsubscribeDateTimes(): void {
        for (const vp of this._dateTimes) {
            vp.unsubscribe(this);
        }
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._list.subscribe(this);
        if (this._order !== undefined) {
            this._order.subscribe(this);
        }
        this.subscribeDateTimes();
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._list.unsubscribe(this);
        if (this._order !== undefined) {
            this._order.unsubscribe(this);
        }
        this.unsubscribeDateTimes();
    }

    protected async recomputeAsync(): Promise<PotentialValue<O, A>> {
        const newDateTimes = new Set<ValueProducer<O, A>>();

        const list = this._list.current;
        if (list === LOADING) return LOADING;

        const order = loadedDefinedMapWithDefault(
            definedMap(this._order, o => o.current),
            CalendarListOrder.OldestFirst,
            v => asString(v) as CalendarListOrder
        );

        const pairs: [Date, ValueProducer<O, A>][] = [];
        for (const item of asArrayValue(list)) {
            const startDateTime = this._startDateTime.eval(item);
            if (!newDateTimes.has(startDateTime)) {
                newDateTimes.add(startDateTime);
                // FIXME: Whether we have subscribers might change during the
                // execution of this method.  This should be fixed in
                // `AsyncRecomputingValueProducer`
                if (this.hasSubscribers) {
                    startDateTime.subscribe(this);
                }
            }

            const dateTimeValue = startDateTime.current;
            if (dateTimeValue === LOADING || dateTimeValue === undefined) continue;

            const date = await parseValueAsDate(dateTimeValue);
            if (date === undefined) continue;

            pairs.push([date, item]);
        }

        const multiplier = order === CalendarListOrder.OldestFirst ? 1 : -1;
        const grouped = flow(
            sortBy(([d, _]: [Date, ValueProducer<O, A>]) => multiplier * d.getTime()),
            groupBy(([d, _]) => formatShortDate(makeStartOfDayTimeZoneAgnostic(d)))
        )(pairs);
        // lodash returns an object whose keys are the group names,
        // which means that the order of the groups is not preserved,
        // so we have to sort the group keys.
        const groupKeys = sortBy((k: string) => multiplier * grouped[k][0][0].getTime())(Object.keys(grouped));

        const result: ValueProducer<O, A>[] = [];
        for (const dateString of groupKeys) {
            const items = grouped[dateString].map(([d, item]) => {
                const o: ObjectValue<O, A> = { start: new Data<O, A>(d), title: this._title.eval(item), item };
                if (this._endDateTime !== undefined) {
                    o.end = this._endDateTime.eval(item);
                }
                if (this._description !== undefined) {
                    o.description = this._description.eval(item);
                }
                if (this._itemID !== undefined) {
                    o.itemID = this._itemID.eval(item);
                }
                return new Data<O, A>(o);
            });
            result.push(new Data({ groupName: new Data<O, A>(dateString), items: new Data<O, A>(items) }));
        }

        if (this.hasSubscribers) {
            for (const vp of this._dateTimes) {
                if (!newDateTimes.has(vp)) {
                    vp.unsubscribe(this);
                }
            }
        }

        this._dateTimes = newDateTimes;

        return result;
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

class SortAndGroupCalendarItemsEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _startDateTime: Evaluable<O, A>,
        private readonly _endDateTime: Evaluable<O, A> | undefined,
        private readonly _title: Evaluable<O, A>,
        private readonly _description: Evaluable<O, A> | undefined,
        private readonly _itemID: Evaluable<O, A> | undefined,
        private readonly _list: Evaluable<O, A>,
        private readonly _order: Evaluable<O, A> | undefined
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new SortAndGroupCalendarItemsValueProducer(
            new BindEnvironmentEvaluable(this._startDateTime, env),
            definedMap(this._endDateTime, e => new BindEnvironmentEvaluable(e, env)),
            new BindEnvironmentEvaluable(this._title, env),
            definedMap(this._description, e => new BindEnvironmentEvaluable(e, env)),
            definedMap(this._itemID, e => new BindEnvironmentEvaluable(e, env)),
            this._list.evalInEnvironment(env, context),
            definedMap(this._order, o => o.evalInEnvironment(env, context))
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return [
            "SortAndGroupCalendarItems",
            "evaluable",
            ["dateTime", "evaluable"],
            ["endDateTime", "evaluable", false],
            ["title", "evaluable"],
            ["description", "evaluable", false],
            ["itemID", "evaluable", false],
            ["list", "evaluable"],
            ["order", "evaluable", false],
        ];
    }
}

class FilterWhereIdenticalEvaluable implements Evaluable<AppOther, AppEnvironment> {
    constructor(
        private readonly _indexName: Evaluable<AppOther, AppEnvironment>,
        private readonly _rows: Evaluable<AppOther, AppEnvironment>,
        private readonly _keyForRow: Evaluable<AppOther, AppEnvironment>,
        private readonly _needle: Evaluable<AppOther, AppEnvironment>,
        private readonly _hostIsArrayType: Evaluable<AppOther, AppEnvironment> | undefined,
        private readonly _targetIsArrayType: Evaluable<AppOther, AppEnvironment> | undefined
    ) {}

    public evalInEnvironment(
        env: EvalEnvironment<AppOther, AppEnvironment>,
        context: Context<AppOther, AppEnvironment>
    ): ValueProducer<AppOther, AppEnvironment> {
        const indexName = loadedDefinedMap(this._indexName.evalInEnvironment(env, context).current, asString);
        assert(indexName !== undefined, "Index must be a constant");

        const hostIsArrayType =
            definedMap(this._hostIsArrayType?.evalInEnvironment(env, context), vp =>
                asMaybeBoolean(getValueFromValueProducer(vp))
            ) ?? false;
        const targetIsArrayType =
            definedMap(this._targetIsArrayType?.evalInEnvironment(env, context), vp =>
                asMaybeBoolean(getValueFromValueProducer(vp))
            ) ?? false;

        const { appEnvironment } = env.root;
        const { tableIndexes } = appEnvironment;

        return new FilterWhereIdenticalValueProducer(
            appEnvironment,
            this._needle.evalInEnvironment(env, context),
            hostIsArrayType,
            () => {
                let index = tableIndexes.get(indexName);
                if (index === undefined) {
                    index = new TableIndex<AppOther, AppEnvironment>(
                        this._rows.evalInEnvironment(env, context),
                        new BindEnvironmentEvaluable(this._keyForRow, env),
                        targetIsArrayType
                    );
                    tableIndexes.set(indexName, index);
                    logInfo("New index", indexName, index);
                }
                return index;
            }
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return [
            "FilterWhereIdentical",
            "evaluable",
            ["indexName", "evaluable"],
            ["rows", "evaluable"],
            ["keyForRow", "evaluable"],
            ["needle", "evaluable"],
            ["hostIsArrayType", "evaluable", false],
            ["targerIsArrayType", "evaluable", false],
        ];
    }
}

class MakeConditionVariableEvaluable implements Evaluable<AppOther, AppEnvironment> {
    public evalInEnvironment(): ValueProducer<AppOther, AppEnvironment> {
        return new Constant<AppOther, AppEnvironment>(new ConditionVariable());
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["MakeConditionVariable", "evaluable"];
    }
}

export function registerGlidePrimitives(): void {
    registerDeserializable(LookupGlobalVariableEvaluable);
    registerDeserializable(GlobalEvaluable);
    registerDeserializable(SortAndGroupCalendarItemsEvaluable);
    registerDeserializable(FilterWhereIdenticalEvaluable);
    registerDeserializable(MakeConditionVariableEvaluable);
}
